import { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
  getDefaultValuesForForm,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { basicSchema } from '../../schemas';
import validationSchema from './validations';
import LaborMatrixTable from '../../components/LaborMatrixTable';

const FORM_ID = 'MAINTENANCE_LABOR_MATRIX_EDIT_FORM';
const PERMISSION_SP = 'LABORMATRIX';

function LaborMatrixEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices/${pageId}`;

  const [loading, setLoading] = useState(true);
  const [basicInfoError, setBasicInfoError] = useState(false);

  const preProcessRequestData = data => {
    const { id, createdBy, lastModifiedBy, ...other } = data;
    return {
      ...other,
    };
  };

  const onSubmit = async data => {
    try {
      setLoading(true);
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'LABORMATRIX_UPDATE_SUCCESS',
            defaultMessage: 'Labor Matrix was saved successfully',
          }),
        );
        setLoading(false);
        return {
          nextValues: response.data,
          errors: null,
        };
      }
      setLoading(false);
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      setLoading(false);
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      setBasicInfoError(false);
      setLoading(true);
      const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/labor-matrices/${pageId}`;
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        setLoading(false);
        return {
          data: {
            ...getDefaultValuesForForm({
              schema: basicSchema,
            }),
            ...response?.data,
          },
          errors: null,
        };
      }

      let errors = [];
      if (response?.errors) {
        errors = [...response.errors];
      }
      setLoading(false);
      setBasicInfoError(true);
      return {
        data: null,
        errors,
      };
    } catch (err) {
      setLoading(false);
      setBasicInfoError(true);
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'LABORMATRICES_BREADCRUMB',
        defaultMessage: 'Labor Matrices',
      }),
      icon: IconLibrary.LaborMatrices,
      onClick: () => {
        navigate('/labormatrices');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_LABORMATRIX_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Labor Matrix',
      }),
      icon: IconLibrary.LaborMatrix,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_LABORMATRICES_STACKABLE_BUTTON',
        defaultMessage: 'Create Labor Matrix',
      }),
      Icon: IconLibrary.LaborMatrix,
      variant: 'outlined',
      onClick: () => navigate('/labormatrices/create'),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
    },
    {
      label: formatMessage({
        id: 'EDIT_LABORMATRICES_STACKABLE_BUTTON',
        defaultMessage: 'List Labor Matrices',
      }),
      Icon: IconLibrary.LaborMatrices,
      variant: 'outlined',
      onClick: () => navigate('/labormatrices'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>

      <PageSection
        label={formatMessage({
          id: 'BASIC_EDIT_HEADING',
          defaultMessage: 'Basic',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={basicSchema}
            validationSchema={validationSchema(formatMessage)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'LABORMATRIX_UPDATE_SAVE_BUTTON',
                  defaultMessage: 'Save Labor Matrix',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'LABORMATRIX_UPDATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>

      {!loading && !basicInfoError && (
        <PageSection
          label={formatMessage({
            id: 'LABORMATRIX_EDIT_HEADING',
            defaultMessage: 'Labor Matrix',
          })}>
          <Box>
            <LaborMatrixTable laborMatrixId={pageId} />
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(LaborMatrixEdit);
